import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { motion } from "framer-motion";
import { IMAGES } from "../lib/content";
import { Reveal } from "./Reveal";

const BANNERS = [
  {
    id: "quem-somos",
    number: "01",
    overline: "Quem somos",
    headline: (
      <>
        Engenharia que executa com{" "}
        <span className="sca-serif font-medium text-sca-red">método, segurança e responsabilidade</span>.
      </>
    ),
    text:
      "Execução técnica organizada para obras industriais e comerciais de alta complexidade.",
    image: IMAGES.bannerQuemSomos,
    to: "/quem-somos",
    align: "left",
    testid: "home-banner-quem-somos",
  },
  {
    id: "obras-industriais",
    number: "02",
    overline: "Obras industriais",
    headline: (
      <>
        Obras industriais exigem{" "}
        <span className="sca-serif font-medium text-sca-red">controle absoluto</span>.
      </>
    ),
    text:
      "Planejamento, organização operacional e execução com rigor técnico.",
    image: IMAGES.bannerObrasIndustriais,
    to: "/servicos/obras-industriais",
    align: "right",
    testid: "home-banner-obras-industriais",
  },
  {
    id: "metodologia",
    number: "03",
    overline: "Metodologia",
    headline: (
      <>
        Obra sem método gera <span className="sca-serif font-medium text-sca-red">atraso, retrabalho e desperdício</span>.
      </>
    ),
    text: "Conheça como a SCA estrutura cada etapa da execução.",
    image: IMAGES.bannerMetodologia,
    to: "/metodologia-de-obra",
    align: "left",
    testid: "home-banner-metodologia",
  },
  {
    id: "contato",
    number: "04",
    overline: "Contato",
    headline: (
      <>
        Sua obra precisa de <span className="sca-serif font-medium text-sca-red">previsibilidade</span>.
      </>
    ),
    text: "Solicite uma avaliação técnica do seu projeto.",
    image: IMAGES.bannerContato,
    to: "/contato",
    align: "right",
    testid: "home-banner-contato",
  },
];

export default function EditorialBanners() {
  return (
    <section
      className="bg-white"
      data-testid="home-editorial-banners"
      aria-label="Banners institucionais"
    >
      {BANNERS.map((b, i) => (
        <BannerRow key={b.id} banner={b} even={i % 2 === 0} />
      ))}
    </section>
  );
}

function BannerRow({ banner, even }) {
  const isLeftText = banner.align === "left";

  return (
    <Link
      to={banner.to}
      data-testid={banner.testid}
      aria-label={`${banner.overline} — ${banner.text}`}
      className={`group relative block overflow-hidden ${
        even ? "bg-white" : "bg-sca-black"
      }`}
    >
      <div
        className={`sca-container grid grid-cols-1 lg:grid-cols-12 gap-0 items-stretch ${
          isLeftText ? "" : "lg:[&>*:first-child]:order-2"
        }`}
      >
        {/* Text side */}
        <div
          className={`lg:col-span-5 flex flex-col justify-center py-16 md:py-24 lg:py-32 ${
            isLeftText ? "lg:pr-12" : "lg:pl-12"
          }`}
        >
          <Reveal>
            <div className="flex items-center gap-4 mb-8">
              <span
                className={`font-mono text-xs tracking-[0.32em] font-bold ${
                  even ? "text-sca-red" : "text-sca-red"
                }`}
              >
                / {banner.number}
              </span>
              <span
                className={`text-[11px] tracking-[0.28em] uppercase font-bold ${
                  even ? "text-sca-steel" : "text-white/60"
                }`}
              >
                {banner.overline}
              </span>
            </div>

            <h2
              className={`font-sans text-3xl sm:text-4xl md:text-5xl lg:text-[54px] font-extrabold leading-[1.06] tracking-tightest mb-8 ${
                even ? "text-sca-black" : "text-white"
              }`}
            >
              {banner.headline}
            </h2>

            <p
              className={`font-sans text-base md:text-lg leading-relaxed max-w-md ${
                even ? "text-sca-steel" : "text-white/75"
              }`}
            >
              {banner.text}
            </p>

            <span
              className={`mt-10 inline-flex items-center gap-3 text-[12px] tracking-[0.22em] uppercase font-bold transition-all group-hover:gap-5 ${
                even ? "text-sca-black group-hover:text-sca-red" : "text-white group-hover:text-sca-red"
              }`}
            >
              Ler mais <ArrowUpRight className="w-4 h-4" />
            </span>
          </Reveal>
        </div>

        {/* Image side */}
        <div className="lg:col-span-7 relative min-h-[380px] md:min-h-[520px] lg:min-h-[600px] overflow-hidden">
          <motion.div
            className="absolute inset-0"
            initial={{ scale: 1 }}
            whileHover={{ scale: 1.04 }}
            transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1] }}
          >
            <img
              src={banner.image}
              alt={banner.overline}
              className="absolute inset-0 w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-[1400ms] ease-out"
              loading="lazy"
              draggable={false}
            />
          </motion.div>
          <div
            className={`absolute inset-0 pointer-events-none ${
              even ? "bg-gradient-to-r from-black/0 to-black/0" : "bg-gradient-to-r from-black/10 to-transparent"
            }`}
          />
          <div className="absolute bottom-6 right-6 md:bottom-8 md:right-8">
            <span className="inline-flex items-center justify-center w-14 h-14 md:w-16 md:h-16 bg-sca-red text-white group-hover:bg-sca-black transition-colors duration-500">
              <ArrowUpRight className="w-6 h-6 md:w-7 md:h-7 group-hover:-translate-y-1 group-hover:translate-x-1 transition-transform duration-500" />
            </span>
          </div>
        </div>
      </div>

      {/* Bottom divider */}
      <div className={`sca-container ${even ? "" : ""}`}>
        <div
          className={`h-px w-full ${even ? "bg-sca-line" : "bg-sca-lineDark"}`}
          aria-hidden
        />
      </div>
    </Link>
  );
}
